const API_URL = process.env.API_URL || 'http://localhost:8004';

class ApiService {
  constructor() {
    this.baseUrl = API_URL;
  }

  getToken() {
    return localStorage.getItem('access_token');
  }

  setToken(token) {
    localStorage.setItem('access_token', token);
  }

  clearToken() {
    localStorage.removeItem('access_token');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.clearToken();
      window.location.href = '/auth/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new Error(error.detail || 'An error occurred');
    }

    return response.json();
  }

  // Config endpoints (public, no auth required)
  async getConfig() {
    const response = await fetch(`${this.baseUrl}/api/config`);
    if (!response.ok) {
      throw new Error('Failed to fetch config');
    }
    return response.json();
  }

  // Auth endpoints
  getGoogleLoginUrl() {
    return `${this.baseUrl}/api/auth/google/login`;
  }

  async getCurrentUser() {
    return this.request('/api/user/me');
  }

  async logout() {
    this.clearToken();
    return this.request('/api/auth/logout', { method: 'POST' });
  }

  // Portfolio endpoints
  async getPortfolioSummary() {
    return this.request('/api/portfolio/summary');
  }

  async getPositions() {
    return this.request('/api/portfolio/positions');
  }

  async getPortfolioHistory(period = '1M') {
    return this.request(`/api/portfolio/history?period=${period}`);
  }

  async getPositionsWithHistory(period = '1M') {
    return this.request(`/api/portfolio/positions/history?period=${period}`);
  }

  // Pie endpoints
  async getPies() {
    return this.request('/api/pies');
  }

  async getPie(pieId) {
    return this.request(`/api/pies/${pieId}`);
  }

  async createPie(pieData) {
    return this.request('/api/pies', {
      method: 'POST',
      body: JSON.stringify(pieData),
    });
  }

  async updatePie(pieId, pieData) {
    return this.request(`/api/pies/${pieId}`, {
      method: 'PUT',
      body: JSON.stringify(pieData),
    });
  }

  async deletePie(pieId) {
    return this.request(`/api/pies/${pieId}`, {
      method: 'DELETE',
    });
  }

  async investInPie(pieId, amount) {
    return this.request(`/api/pies/${pieId}/invest?amount=${amount}`, {
      method: 'POST',
    });
  }

  async rebalancePie(pieId) {
    return this.request(`/api/pies/${pieId}/rebalance/execute`, {
      method: 'POST',
    });
  }

  // Pie allocation configuration
  async getPieAllocations() {
    return this.request('/api/pies/allocations');
  }

  async updatePieAllocations(allocations) {
    return this.request('/api/pies/allocations', {
      method: 'PUT',
      body: JSON.stringify({ allocations }),
    });
  }

  async investAcrossPies(amount) {
    return this.request(`/api/pies/invest?amount=${amount}`, {
      method: 'POST',
    });
  }

  async rebalanceAllPies() {
    return this.request('/api/pies/rebalance', {
      method: 'POST',
    });
  }

  // Order endpoints
  async getOrders(status = null, limit = 50) {
    let url = `/api/orders?limit=${limit}`;
    if (status) {
      url += `&status=${status}`;
    }
    return this.request(url);
  }

  async getOrder(orderId) {
    return this.request(`/api/orders/${orderId}`);
  }

  async cancelOrder(orderId) {
    return this.request(`/api/orders/${orderId}`, {
      method: 'DELETE',
    });
  }

  // Market data endpoints
  async searchStocks(query, limit = 10) {
    const response = await this.request(`/api/market/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.assets || [];
  }

  async getStockInfo(symbol) {
    return this.request(`/api/market/asset/${encodeURIComponent(symbol)}`);
  }

  async getStockQuote(symbol) {
    return this.request(`/api/market/quote/${encodeURIComponent(symbol)}`);
  }

  async getQuote(symbol) {
    return this.request(`/api/market/quote/${symbol}`);
  }

  async getQuotes(symbols) {
    return this.request(`/api/market/quotes?symbols=${symbols.join(',')}`);
  }

  async getBars(symbol, timeframe = '1Day', limit = 30) {
    return this.request(`/api/market/bars/${symbol}?timeframe=${timeframe}&limit=${limit}`);
  }

  // Order endpoints (for direct trading)
  async placeOrder(orderData) {
    return this.request('/api/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  // Onboarding endpoints
  async getOnboardingStatus() {
    return this.request('/api/onboarding/status');
  }

  async submitOnboarding(data) {
    return this.request('/api/onboarding/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Sell from pies (reverse of invest)
  async sellFromPies(amount) {
    return this.request(`/api/pies/sell?amount=${amount}`, {
      method: 'POST',
    });
  }

  // ACH Relationships and Transfers
  async getAchRelationships() {
    return this.request('/api/portfolio/ach-relationships');
  }

  async createAchRelationship(data) {
    return this.request('/api/portfolio/ach-relationships', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAchRelationship(relationshipId) {
    return this.request(`/api/portfolio/ach-relationships/${relationshipId}`, {
      method: 'DELETE',
    });
  }

  async deposit(amount, relationshipId) {
    return this.request('/api/portfolio/deposit', {
      method: 'POST',
      body: JSON.stringify({ amount, relationship_id: relationshipId }),
    });
  }

  async withdraw(amount, relationshipId) {
    return this.request('/api/portfolio/withdraw', {
      method: 'POST',
      body: JSON.stringify({ amount, relationship_id: relationshipId }),
    });
  }

  async getTransfers() {
    return this.request('/api/portfolio/transfers');
  }

  // Get account info (includes cash balance)
  async getAccountInfo() {
    return this.request('/api/portfolio/account');
  }

  // Documents endpoints
  async getDocuments(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.document_type) queryParams.append('document_type', params.document_type);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/api/documents?${queryString}` : '/api/documents';
    return this.request(url);
  }

  async getDocument(documentId) {
    return this.request(`/api/documents/${documentId}`);
  }

  // Waitlist endpoints (public, no auth required)
  async joinWaitlist(data) {
    const response = await fetch(`${this.baseUrl}/api/waitlist/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.detail || 'Failed to join waitlist');
    }
    
    return result;
  }
}

export const api = new ApiService();
export default api;